/* istanbul ignore file */
import React from "react";
import { bedragFormat } from "../../../shared/utils/currency";
import Table from "../components/Table";
import { Column } from "../components/BaseTable";
import "./nbi.css";

export interface NbiItem {
  jaar: any;
  leeftijd1: number | string | null;
  leeftijd2: number | string | null;
  inkomenBedrag: number | null;
  uitgavenBedrag: number | null;
  nbiBedrag: number | null;
  gewenstNbiBedrag: number | null;
  tekortOverschotBedrag: number | null;
  bijzonderheden?: Array<{ bijzonderheid: string } | null> | null;
}

interface TableNettoBesteedbaarInkomenProps {
  nbi: NbiItem[] | null | undefined;
  voetnoot?: string | null;
  startDatum?: any;
}

export const TableNettoBesteedbaarInkomen: React.FC<TableNettoBesteedbaarInkomenProps> = ({
  nbi,
  voetnoot,
  startDatum,
}) => {
  if (!nbi || nbi.length === 0) return <></>;

  function calculateYear(startDatumInternal: any, jaar: any): React.ReactNode {
    if (startDatumInternal && typeof jaar === "number") {
      return new Date(startDatumInternal).getFullYear() + jaar;
    }
    return jaar?.toString() || "N/A";
  }

  const columns: Column[] = [
    { header: "Jaar", accessor: "jaarCalculated", className: "text-center" },
    {
      header: "Leeftijd",
      accessor: "leeftijdCombined",
      className: "text-center",
    },
    {
      header: "Inkomsten",
      accessor: "inkomstenFormatted",
      className: "text-right",
    },
    {
      header: "Uitgaven",
      accessor: "uitgavenFormatted",
      className: "text-right",
    },
    {
      header: "Netto besteed-baar",
      accessor: "nbiFormatted",
      className: "text-right",
    },
    {
      header: "Gewenst netto besteedbaar",
      accessor: "gewenstNbiFormatted",
      className: "text-right",
    },
    {
      header: "Tekort / overschot",
      accessor: "tekortOverschotFormatted",
      className: "text-right",
    },
    {
      header: "Bijzonderheid",
      accessor: (row: NbiItem) =>
        row.bijzonderheden?.map((c) => c?.bijzonderheid).filter(Boolean) || [],
    },
  ];

  const data = nbi.map((item) => ({
    ...item,
    jaarCalculated: calculateYear(startDatum, item.jaar - 1),
    leeftijdCombined: (() => {
      const age1 = item.leeftijd1?.toString() || "";
      const age2 = item.leeftijd2?.toString() || "";
      if (age1 && age2) return `${age1} / ${age2}`;
      if (age1) return age1;
      if (age2) return age2;
      return "";
    })(),
    inkomstenFormatted: bedragFormat(
      item.inkomenBedrag ? item.inkomenBedrag / 12 : 0
    ),
    uitgavenFormatted: bedragFormat(
      item.uitgavenBedrag ? item.uitgavenBedrag / 12 : 0
    ),
    nbiFormatted: bedragFormat(item.nbiBedrag ? item.nbiBedrag / 12 : 0),
    gewenstNbiFormatted: bedragFormat(
      item.gewenstNbiBedrag ? item.gewenstNbiBedrag / 12 : 0
    ),
    tekortOverschotFormatted: bedragFormat(
      item.tekortOverschotBedrag ? item.tekortOverschotBedrag / 12 : 0
    ),
    bijzonderhedenText:
      (
        item.bijzonderheden?.map((c) => c?.bijzonderheid).filter(Boolean) || []
      ).join(", ") || "",
  }));

  const onlyContainsWijzigingBelastbaarInkomen = (
    bijzonderheden: any[] | null | undefined
  ): boolean => {
    if (!bijzonderheden || bijzonderheden.length === 0) return false;
    return (
      bijzonderheden.filter((b) => b.bijzonderheid || "").length === 1 &&
      bijzonderheden[0].bijzonderheid?.startsWith(
        "Wijziging belastbaar inkomen"
      )
    );
  };

  const areRowsEqualOnDisplayData = (a: any, b: any): boolean => {
    return (
      (a.gewenstNbiFormatted === b.gewenstNbiFormatted &&
        a.tekortOverschotFormatted === b.tekortOverschotFormatted &&
        (a.bijzonderheden?.filter(
          (c: any) =>
            c.bijzonderheid !== "" &&
            !c.bijzonderheid?.startsWith("Wijziging belastbaar inkomen")
        )?.length ?? 0) < 1) ||
      (a.inkomstenFormatted === b.inkomstenFormatted &&
        onlyContainsWijzigingBelastbaarInkomen(a.bijzonderheden) &&
        onlyContainsWijzigingBelastbaarInkomen(b.bijzonderheden))
    );
  };

  const filteredData = data.filter((row, index, arr) => {
    if (index === 0) return true;
    return !areRowsEqualOnDisplayData(row, arr[index - 1]);
  });

  return (
    <>
      <Table
        columns={columns}
        data={filteredData}
        className="netto-besteedbaar-inkomen-table"
        compact
      />
      {voetnoot && (
        <div
          className="text-left font-italic mt-2"
          style={{ captionSide: "bottom" }}
        >
          {/* Simulating tfoot content. Proper tfoot support might require Table component enhancement or specific props. */}
          {voetnoot}
        </div>
      )}
    </>
  );
};
